'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import {
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  ChevronRight,
  Coins,
  FileText,
  Globe2,
  Images,
  LockKeyhole,
  Menu,
  MessageCircle,
  NotebookPen,
  ShieldCheck,
  Instagram,
  Mail,
  Sparkles,
  Star,
  Users,
  X,
  Youtube,
} from 'lucide-react';
import { AccentText, PublicLanguageSwitcher, useI18n } from '@/lib/i18n';

function Logo({ className = 'h-10 w-10' }: { className?: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img src="/logo.svg" alt="Bevelure" className={className} />
  );
}

const APP_STORE_URL = 'https://apps.apple.com/app/bevelure/id6806039309';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bevelure.app';

function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7 flex-shrink-0" aria-hidden>
      <path d="M17.05 12.54c-.03-3.02 2.47-4.49 2.58-4.56a5.54 5.54 0 0 0-4.35-2.35c-1.83-.19-3.6 1.1-4.53 1.1-.95 0-2.39-1.08-3.94-1.05a5.78 5.78 0 0 0-4.87 2.97c-2.11 3.65-.54 9.02 1.48 11.97 1.01 1.45 2.18 3.07 3.73 3.01 1.51-.06 2.08-.97 3.91-.97 1.81 0 2.35.97 3.93.94 1.63-.03 2.66-1.45 3.63-2.91a11.94 11.94 0 0 0 1.66-3.39 5.22 5.22 0 0 1-3.23-4.76ZM14.08 3.69A5.3 5.3 0 0 0 15.29 0a5.4 5.4 0 0 0-3.49 1.75 5.05 5.05 0 0 0-1.24 3.55 4.45 4.45 0 0 0 3.52-1.61Z" />
    </svg>
  );
}

function GooglePlayGlyph() {
  return (
    <svg viewBox="0 0 32 36" className="h-7 w-7 flex-shrink-0" aria-hidden>
      <path fill="#34A853" d="M1.2 1.2A3 3 0 0 0 .5 3.3v29.4a3 3 0 0 0 .7 2.1L18.3 18Z" />
      <path fill="#4285F4" d="m22.8 13.6-17-12A3.2 3.2 0 0 0 3 1l15.3 17Z" />
      <path fill="#FBBC04" d="m22.8 22.4-4.5-4.4L3 35a3.2 3.2 0 0 0 2.8-.6Z" />
      <path fill="#EA4335" d="m30 15.8-7.2-2.2-4.5 4.4 4.5 4.4 7.2-2.2a2.5 2.5 0 0 0 0-4.4Z" />
    </svg>
  );
}

function StoreButtons({ className = '' }: { className?: string }) {
  const { t } = useI18n();
  const stores = [
    {
      href: APP_STORE_URL,
      eyebrow: t('landing.stores.appStoreEyebrow'),
      name: t('landing.stores.appStoreName'),
      icon: <AppleGlyph />,
    },
    {
      href: PLAY_STORE_URL,
      eyebrow: t('landing.stores.googlePlayEyebrow'),
      name: t('landing.stores.googlePlayName'),
      icon: <GooglePlayGlyph />,
    },
  ];

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {stores.map((store) => (
        <a
          key={store.href}
          href={store.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${store.eyebrow} ${store.name}`}
          className="inline-flex h-14 min-w-[172px] items-center gap-3 rounded-2xl border border-white/20 bg-black px-4 text-left text-white shadow-[0_14px_32px_rgba(0,0,0,0.2)] transition-all hover:-translate-y-0.5 hover:bg-stone-900"
        >
          {store.icon}
          <span>
            <span className="block text-[10px] leading-none text-stone-300">{store.eyebrow}</span>
            <span className="mt-1 block text-lg font-semibold leading-none">{store.name}</span>
          </span>
        </a>
      ))}
    </div>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();

  const navLinks = [
    { href: '#platform', label: t('landing.nav.platform') },
    { href: '#features', label: t('landing.nav.features') },
    { href: '#story', label: t('landing.nav.story') },
    { href: '#trust', label: t('landing.nav.trust') },
  ];

  return (
    <nav className="fixed inset-x-0 top-[var(--app-banner-offset,0px)] z-50 border-b border-white/60 bg-[#FFFCF8]/90 backdrop-blur-xl transition-[top]">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <Link href="/" className="flex items-center gap-3" aria-label={t('common.homeAria')}>
            <Logo className="h-9 w-9" />
            <span className="font-serif text-2xl font-semibold text-ink-900">Bevelure</span>
          </Link>
        </div>

        <div className="hidden items-center gap-8 lg:flex">
          {navLinks.map((item) => (
            <a key={item.href} href={item.href} className="text-sm font-medium text-stone-600 transition-colors hover:text-ink-900">
              {item.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-2 md:flex">
          <PublicLanguageSwitcher showLabel={false} />
          <Link href="/login" className="px-4 py-2 text-sm font-semibold text-stone-700 transition-colors hover:text-ink-900">
            {t('auth.login')}
          </Link>
          <Link
            href="/signup"
            className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-ink-900 px-5 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(33,27,25,0.18)] transition-all hover:-translate-y-0.5 hover:bg-ink-950"
          >
            {t('landing.nav.startNow')} <ArrowRight size={16} />
          </Link>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <PublicLanguageSwitcher showLabel={false} />
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[#E8DDD5] text-stone-700"
            onClick={() => setOpen((v) => !v)}
            aria-label={t('landing.nav.openMenu')}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-[#E8DDD5] bg-[#FFFCF8] px-5 py-4 shadow-xl md:hidden">
          <div className="flex flex-col gap-3">
            {navLinks.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="rounded-2xl px-3 py-2 text-sm font-semibold text-stone-700 hover:bg-white"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </a>
            ))}
            <div className="my-1 h-px bg-[#E8DDD5]" />
            <Link href="/login" className="rounded-2xl px-3 py-2 text-sm font-semibold text-stone-700" onClick={() => setOpen(false)}>
              {t('auth.login')}
            </Link>
            <Link
              href="/signup"
              className="rounded-2xl bg-ink-900 px-4 py-3 text-center text-sm font-semibold text-white"
              onClick={() => setOpen(false)}
            >
              {t('landing.nav.startNow')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

function ProductPanel() {
  const { t } = useI18n();
  const rows = [
    { label: t('landing.panel.row1Label'), meta: t('landing.panel.row1Meta'), icon: <NotebookPen size={16} /> },
    { label: t('landing.panel.row2Label'), meta: t('landing.panel.row2Meta'), icon: <FileText size={16} /> },
    { label: t('landing.panel.row3Label'), meta: t('landing.panel.row3Meta'), icon: <MessageCircle size={16} /> },
  ];

  return (
    <div className="hidden lg:block">
      <div className="luxury-surface w-[390px] p-4 text-ink-900">
        <div className="mb-4 flex items-center justify-between border-b border-[#E8DDD5] pb-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400">{t('date.today')}</p>
            <p className="mt-1 font-serif text-2xl font-semibold">{t('landing.panel.title')}</p>
          </div>
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-customer-50 text-customer-700">
            <Sparkles size={18} />
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            ['18', t('landing.panel.visits')],
            ['6', t('landing.panel.providers')],
            ['4.9', t('landing.panel.rating')],
          ].map(([value, label]) => (
            <div key={label} className="rounded-2xl border border-[#E8DDD5] bg-white/50 p-3">
              <p className="text-xl font-bold text-ink-900">{value}</p>
              <p className="mt-1 text-xs text-stone-500">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 space-y-2">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center gap-3 rounded-2xl border border-[#E8DDD5] bg-white/60 p-3">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-champagne-100 text-champagne-500">
                {row.icon}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink-900">{row.label}</p>
                <p className="truncate text-xs text-stone-500">{row.meta}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const { t } = useI18n();
  return (
    <section className="relative min-h-[88svh] overflow-hidden bg-ink-950 pt-24 text-white">
      <Image
        src="/landing-assets/bevelure-luxury-hero.png"
        alt=""
        fill
        priority
        sizes="100vw"
        className="object-cover object-center"
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(20,17,15,0.88)_0%,rgba(20,17,15,0.72)_38%,rgba(20,17,15,0.24)_74%,rgba(20,17,15,0.14)_100%)]" />

      <div className="relative z-10 mx-auto grid min-h-[calc(88svh-6rem)] max-w-7xl items-center gap-10 px-5 pb-16 sm:px-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(360px,0.65fr)] lg:px-8">
        <div className="max-w-2xl">
          <div className="mb-6 inline-flex max-w-full items-center gap-2 border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-champagne-100 backdrop-blur sm:text-xs sm:tracking-[0.18em]">
            <Sparkles size={13} className="flex-shrink-0" />
            <span className="min-w-0 [overflow-wrap:anywhere]">{t('landing.hero.eyebrow')}</span>
          </div>

          <h1 className="font-serif text-6xl font-semibold leading-none text-white sm:text-7xl lg:text-8xl">
            Bevelure
          </h1>
          <p className="mt-5 max-w-xl text-xl leading-8 text-stone-100 sm:text-2xl">
            {t('landing.hero.body')}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              href="/signup"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-white px-6 text-sm font-bold text-ink-900 shadow-[0_18px_40px_rgba(0,0,0,0.22)] transition-all hover:-translate-y-0.5 hover:bg-champagne-50"
            >
              {t('landing.cta.startJourney')} <ChevronRight size={17} />
            </Link>
          </div>

          {/* One tile per side of the product — signals from the first screen
              that Bevelure serves clients, providers, and salons. */}
          <div className="mt-8 grid max-w-xl grid-cols-3 gap-2 sm:gap-3">
            {[
              [t('landing.hero.tile1Title'), t('landing.hero.tile1Body')],
              [t('landing.hero.tile2Title'), t('landing.hero.tile2Body')],
              [t('landing.hero.tile3Title'), t('landing.hero.tile3Body')],
            ].map(([value, label]) => (
              <div key={label} className="min-w-0 border border-white/20 bg-white/10 p-2.5 backdrop-blur sm:p-3">
                <p className="font-serif text-[clamp(1rem,4.6vw,1.5rem)] font-semibold leading-[1.08] text-white [overflow-wrap:anywhere]">{value}</p>
                <p className="mt-1 text-xs leading-4 text-stone-300 [overflow-wrap:anywhere]">{label}</p>
              </div>
            ))}
          </div>
        </div>

        <ProductPanel />
      </div>
    </section>
  );
}

function TrustBar() {
  const { t } = useI18n();
  const items = [
    { icon: <ShieldCheck size={16} />, label: t('landing.trustBar.item1') },
    { icon: <Sparkles size={16} />, label: t('landing.trustBar.item2') },
    { icon: <Building2 size={16} />, label: t('landing.trustBar.item3') },
  ];

  return (
    <section className="border-y border-[#E8DDD5] bg-[#FFFCF8] px-5 py-5">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-12 gap-y-3 sm:px-6 lg:px-8">
        {items.map((item) => (
          <div key={item.label} className="flex items-center gap-2 text-sm font-semibold text-stone-700">
            <span className="text-champagne-500">{item.icon}</span>
            {item.label}
          </div>
        ))}
      </div>
    </section>
  );
}

/* The thesis: one shared record that means something different to each side.
   This is the spine that makes the three-audience story cohere. */
function ThreeSides() {
  const { t } = useI18n();
  const sides = [
    {
      tag: t('landing.three.side1Tag'),
      icon: <Users size={18} />,
      title: t('landing.three.side1Title'),
      desc: t('landing.three.side1Desc'),
    },
    {
      tag: t('landing.three.side2Tag'),
      icon: <Sparkles size={18} />,
      title: t('landing.three.side2Title'),
      desc: t('landing.three.side2Desc'),
    },
    {
      tag: t('landing.three.side3Tag'),
      icon: <Building2 size={18} />,
      title: t('landing.three.side3Title'),
      desc: t('landing.three.side3Desc'),
    },
  ];

  return (
    <section className="bg-[#F6EEE7] px-5 py-24">
      <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-customer-700">{t('landing.three.eyebrow')}</p>
          <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight text-ink-900 sm:text-5xl">
            {t('landing.three.title')}
          </h2>
          <p className="mt-5 text-base leading-7 text-stone-600">
            {t('landing.three.body')}
          </p>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {sides.map((side) => (
            <article key={side.tag} className="border border-[#E8DDD5] bg-[#FFFCF8] p-6 shadow-[0_16px_42px_rgba(33,27,25,0.06)]">
              <span className="mb-5 flex h-10 w-10 items-center justify-center rounded-2xl bg-champagne-100 text-champagne-500">
                {side.icon}
              </span>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-customer-700">{side.tag}</p>
              <h3 className="mt-2 font-serif text-xl font-semibold leading-tight text-ink-900">{side.title}</h3>
              <p className="mt-3 text-sm leading-6 text-stone-600">{side.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Per-audience deep dive — each side gets a full panel with its own
   emotional headline, concrete benefits, and a direct path to sign up. */
function Audiences() {
  const { t } = useI18n();
  const audiences = [
    {
      tag: t('landing.audiences.clientTag'),
      icon: <Users size={18} />,
      title: t('landing.audiences.clientTitle'),
      lead: t('landing.audiences.clientLead'),
      points: [
        t('landing.audiences.clientPoint1'),
        t('landing.audiences.clientPoint2'),
        t('landing.audiences.clientPoint3'),
        t('landing.audiences.clientPoint4'),
      ],
      cta: t('landing.audiences.clientCta'),
      href: '/signup/customer',
    },
    {
      tag: t('landing.audiences.providerTag'),
      icon: <Sparkles size={18} />,
      title: t('landing.audiences.providerTitle'),
      lead: t('landing.audiences.providerLead'),
      points: [
        t('landing.audiences.providerPoint1'),
        t('landing.audiences.providerPoint2'),
        t('landing.audiences.providerPoint3'),
        t('landing.audiences.providerPoint4'),
      ],
      cta: t('landing.audiences.providerCta'),
      href: '/signup/provider',
    },
    {
      tag: t('landing.audiences.salonTag'),
      icon: <Building2 size={18} />,
      title: t('landing.audiences.salonTitle'),
      lead: t('landing.audiences.salonLead'),
      points: [
        t('landing.audiences.salonPoint1'),
        t('landing.audiences.salonPoint2'),
        t('landing.audiences.salonPoint3'),
        t('landing.audiences.salonPoint4'),
      ],
      cta: t('landing.audiences.salonCta'),
      href: '/signup/salon',
    },
  ];

  return (
    <section id="platform" className="bg-[#FFFCF8] px-5 py-24">
      <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-customer-700">{t('landing.audiences.eyebrow')}</p>
          <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight text-ink-900 sm:text-5xl">
            {t('landing.audiences.title')}
          </h2>
        </div>

        <div className="mt-12 space-y-6">
          {audiences.map((a, i) => (
            <article
              key={a.tag}
              className="overflow-hidden border border-[#E8DDD5] bg-[#FFFCF8] shadow-[0_16px_42px_rgba(33,27,25,0.07)]"
            >
              <div className={`grid gap-0 lg:grid-cols-2 ${i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                <div className="flex flex-col justify-center p-8 lg:p-10">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-champagne-100 text-champagne-500">
                      {a.icon}
                    </span>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-customer-700">{a.tag}</p>
                  </div>
                  <h3 className="font-serif text-3xl font-semibold leading-tight text-ink-900">{a.title}</h3>
                  <p className="mt-4 text-base leading-7 text-stone-600">{a.lead}</p>
                  <Link
                    href={a.href}
                    className="mt-7 inline-flex w-fit items-center gap-2 rounded-2xl bg-ink-900 px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-ink-950"
                  >
                    {a.cta} <ChevronRight size={16} />
                  </Link>
                </div>

                <div className="border-t border-[#E8DDD5] bg-[#F6EEE7] p-8 lg:border-l lg:border-t-0 lg:p-10">
                  <ul className="space-y-4">
                    {a.points.map((point) => (
                      <li key={point} className="flex gap-3 text-base leading-6 text-stone-700">
                        <Check size={18} className="mt-0.5 flex-shrink-0 text-customer-700" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* The cross-cutting capabilities — one deduped grid (formerly two
   near-identical card grids). The genuinely interesting stuff only. */
function Capabilities() {
  const { t } = useI18n();
  const features = [
    {
      icon: <FileText size={19} />,
      title: t('landing.features.item1Title'),
      desc: t('landing.features.item1Desc'),
    },
    {
      icon: <CalendarDays size={19} />,
      title: t('landing.features.item2Title'),
      desc: t('landing.features.item2Desc'),
    },
    {
      icon: <LockKeyhole size={19} />,
      title: t('landing.features.item3Title'),
      desc: t('landing.features.item3Desc'),
    },
    {
      icon: <Images size={19} />,
      title: t('landing.features.item4Title'),
      desc: t('landing.features.item4Desc'),
    },
    {
      icon: <Globe2 size={19} />,
      title: t('landing.features.item5Title'),
      desc: t('landing.features.item5Desc'),
    },
    {
      icon: <MessageCircle size={19} />,
      title: t('landing.features.item6Title'),
      desc: t('landing.features.item6Desc'),
    },
    {
      icon: <Coins size={19} />,
      title: t('landing.features.item7Title'),
      desc: t('landing.features.item7Desc'),
    },
    {
      icon: <Star size={19} />,
      title: t('landing.features.item8Title'),
      desc: t('landing.features.item8Desc'),
    },
  ];

  return (
    <section id="features" className="bg-[#FFFCF8] px-5 py-24">
      <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[0.82fr_1fr] lg:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-champagne-500">{t('landing.features.eyebrow')}</p>
            <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight text-ink-900 sm:text-5xl">
              {t('landing.features.title')}
            </h2>
          </div>
          <p className="max-w-2xl text-base leading-7 text-stone-600">
            {t('landing.features.body')}
          </p>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {features.map((feature) => (
            <article key={feature.title} className="luxury-card p-5">
              <span className="mb-5 flex h-10 w-10 items-center justify-center rounded-2xl bg-customer-50 text-customer-700">
                {feature.icon}
              </span>
              <h3 className="font-serif text-xl font-semibold leading-tight text-ink-900">{feature.title}</h3>
              <p className="mt-3 text-sm leading-6 text-stone-600">{feature.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FounderStory() {
  const { t } = useI18n();
  return (
    <section id="story" className="bg-[#F6EEE7] px-5 py-24">
      <div className="mx-auto grid max-w-7xl gap-12 sm:px-6 lg:grid-cols-[0.88fr_1.12fr] lg:items-start lg:px-8">
        <div className="relative lg:sticky lg:top-28">
          <div className="absolute -left-4 -top-4 hidden h-full w-full border border-champagne-200 md:block" />
          <div className="relative overflow-hidden border border-[#E8DDD5] bg-[#FFFCF8] p-3 shadow-[0_24px_64px_rgba(33,27,25,0.12)]">
            <div className="relative aspect-[4/5] overflow-hidden">
              <Image
                src="/landing-assets/eve-andrieieva.jpeg"
                alt="Eve Andrieieva, Founder and CEO of Bevelure"
                fill
                sizes="(min-width: 1024px) 430px, 100vw"
                className="object-cover object-center"
              />
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-champagne-500">{t('landing.founder.eyebrow')}</p>
          <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight text-ink-900 sm:text-5xl">
            {t('landing.founder.title')}
          </h2>
          <div className="mt-6 space-y-4 text-base leading-7 text-stone-700">
            <p><AccentText text={t('landing.founder.p1')} accentClassName="italic" /></p>
            <p><AccentText text={t('landing.founder.p2')} accentClassName="italic" /></p>
            <p>{t('landing.founder.p3')}</p>
            <p>{t('landing.founder.p4')}</p>
            <p><AccentText text={t('landing.founder.p5')} accentClassName="italic" /></p>
            <p>{t('landing.founder.p6')}</p>
          </div>

          <blockquote className="mt-8 border-l border-champagne-300 pl-5">
            <p className="font-serif text-2xl font-medium leading-snug text-ink-900">
              {t('landing.founder.quote')}
            </p>
            <footer className="mt-3 text-sm font-semibold text-stone-600">{t('landing.founder.signature')}</footer>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

function Trust() {
  const { t } = useI18n();
  const items = [
    { icon: <ShieldCheck size={18} />, title: t('landing.trust.item1Title'), desc: t('landing.trust.item1Desc') },
    { icon: <MessageCircle size={18} />, title: t('landing.trust.item2Title'), desc: t('landing.trust.item2Desc') },
    { icon: <Star size={18} />, title: t('landing.trust.item3Title'), desc: t('landing.trust.item3Desc') },
  ];

  return (
    <section id="trust" className="luxury-dark px-5 py-24 text-white">
      <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-champagne-200">{t('landing.trust.eyebrow')}</p>
            <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight sm:text-5xl">
              {t('landing.trust.title')}
            </h2>
          </div>
          <p className="max-w-2xl text-base leading-7 text-stone-300">
            {t('landing.trust.body')}
          </p>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {items.map((item) => (
            <article key={item.title} className="border border-white/10 bg-white/5 p-6 backdrop-blur">
              <span className="mb-5 flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-champagne-200">
                {item.icon}
              </span>
              <h3 className="font-serif text-2xl font-semibold">{item.title}</h3>
              <p className="mt-3 text-sm leading-6 text-stone-300">{item.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useI18n();
  return (
    <section className="bg-[#FFFCF8] px-5 py-20">
      <div className="mx-auto max-w-4xl border border-[#E8DDD5] bg-white/60 p-8 text-center shadow-[0_18px_54px_rgba(33,27,25,0.08)] sm:p-12">
        <Logo className="mx-auto h-14 w-14" />
        <h2 className="mt-5 font-serif text-4xl font-semibold leading-tight text-ink-900 sm:text-5xl">
          {t('landing.final.title')}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-stone-600">
          {t('landing.final.body')}
        </p>
        <div className="mt-8 flex justify-center">
          <Link href="/signup" className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-ink-900 px-7 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-ink-950">
            {t('landing.cta.startJourney')} <ChevronRight size={17} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// Compact glyphs for socials Lucide doesn't ship. Currentcolor follows the
// surrounding link styling so the icons match the rest of the footer.
function TikTokGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.27a8.16 8.16 0 0 0 4.77 1.52V6.33a4.85 4.85 0 0 1-1.85-.36z" />
    </svg>
  );
}

function XGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const SOCIALS: { label: string; href: string; icon: React.ReactNode }[] = [
  { label: 'Instagram', href: 'https://www.instagram.com/bevelure_beauty/', icon: <Instagram size={18} /> },
  { label: 'TikTok', href: 'https://www.tiktok.com/@bevelure_beauty?lang=en-GB', icon: <TikTokGlyph className="h-[18px] w-[18px]" /> },
  { label: 'X', href: 'https://x.com/bevelure_beauty', icon: <XGlyph className="h-[16px] w-[16px]" /> },
  { label: 'YouTube', href: 'https://www.youtube.com/@bevelure_beauty', icon: <Youtube size={19} /> },
];

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-[#E8DDD5] bg-[#FFFCF8] px-5 py-10">
      <div className="mx-auto max-w-7xl text-sm text-stone-500 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-start gap-6 text-center sm:grid-cols-3 sm:text-left">
          <div className="flex flex-col items-center gap-2 sm:items-start sm:text-left">
            <div className="flex items-center gap-3">
              <Logo className="h-8 w-8" />
              <span className="font-serif text-xl font-semibold text-ink-900">Bevelure</span>
            </div>
            <p className="text-xs text-stone-500">{t('landing.footer.tagline')}</p>
          </div>

          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">{t('landing.footer.contact')}</p>
            <a
              href="mailto:support@bevelure.com"
              className="inline-flex items-center gap-2 text-ink-900 hover:text-ink-950"
            >
              <Mail size={15} className="text-stone-500" />
              support@bevelure.com
            </a>
            <div className="mt-1 flex items-center gap-2">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E8DDD5] bg-white text-stone-600 transition-colors hover:border-ink-900 hover:text-ink-900"
                >
                  {s.icon}
                </a>
              ))}
            </div>
            <StoreButtons className="mt-3 justify-center" />
          </div>

          <div className="flex flex-col items-center gap-2 sm:items-end sm:text-right">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">{t('landing.footer.site')}</p>
            <Link href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-ink-900">{t('nav.terms')}</Link>
            <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-ink-900">{t('nav.privacy')}</Link>
            <Link href="/login" className="hover:text-ink-900">{t('auth.login')}</Link>
          </div>
        </div>

        <div className="mt-8 border-t border-[#E8DDD5] pt-4 text-center text-xs text-stone-400">
          © 2026 Bevelure
        </div>
      </div>
    </footer>
  );
}

export default function LandingClient() {
  return (
    <>
      <Navbar />
      <Hero />
      <TrustBar />
      <ThreeSides />
      <Audiences />
      <Capabilities />
      <FounderStory />
      <Trust />
      <FinalCTA />
      <Footer />
    </>
  );
}
